/**
 * StandingStonesLandmark - Norse standing stone circle landmark
 * 
 * A simple 6-8 stone circle for the Phase 3 continuous travel prototype.
 * Located ~200 units from Thornhaven in the wilderness.
 */

import { useMemo } from 'react';
import * as THREE from 'three';
import { RigidBody, CuboidCollider } from '@react-three/rapier';

// Stone configuration
const STONE_COUNT = 7;
const CIRCLE_RADIUS = 8;
const STONE_BASE_HEIGHT = 3.5;
const STONE_WIDTH_BASE = 0.8;

// Deterministic pseudo-random for consistent stone placement
function seededRandom(seed) {
  const x = Math.sin(seed * 12.9898 + seed * 78.233) * 43758.5453;
  return x - Math.floor(x);
}

function StandingStone({ position, rotation, height, width, tilt }) {
  // Create slightly irregular stone geometry
  const geometry = useMemo(() => {
    const geo = new THREE.BoxGeometry(width, height, width * 0.6);
    // Add slight taper at top
    const positions = geo.attributes.position.array;
    for (let i = 0; i < positions.length; i += 3) {
      const y = positions[i + 1];
      if (y > 0) {
        const taperFactor = 1 - (y / height) * 0.3;
        positions[i] *= taperFactor;
        positions[i + 2] *= taperFactor;
      }
    }
    geo.attributes.position.needsUpdate = true;
    geo.computeVertexNormals();
    return geo;
  }, [height, width]);

  return (
    <RigidBody type="fixed" position={position} rotation={[tilt, rotation, 0]} colliders={false}>
      <CuboidCollider args={[width / 2, height / 2, width * 0.3]} position={[0, height / 2, 0]} />
      <mesh geometry={geometry} position={[0, height / 2, 0]} castShadow receiveShadow>
        <meshStandardMaterial 
          color="#4a5568" 
          roughness={0.9} 
          metalness={0.1}
        />
      </mesh>
    </RigidBody>
  );
}

function CenterAltar({ position }) {
  return (
    <RigidBody type="fixed" position={position} colliders={false}>
      <CuboidCollider args={[1.2, 0.3, 0.8]} position={[0, 0.3, 0]} />
      <mesh position={[0, 0.3, 0]} castShadow receiveShadow>
        <boxGeometry args={[2.4, 0.6, 1.6]} />
        <meshStandardMaterial color="#374151" roughness={0.85} metalness={0.1} />
      </mesh>
    </RigidBody>
  );
}

export default function StandingStonesLandmark({ position = [0, 0, -175] }) {
  // Generate stone positions in a circle with deterministic variation
  // Using seeded random ensures consistent layout on chunk reload
  const stones = useMemo(() => {
    const result = [];
    for (let i = 0; i < STONE_COUNT; i++) {
      const angle = (i / STONE_COUNT) * Math.PI * 2;
      // Use deterministic variation based on stone index
      const radiusVariation = (seededRandom(i * 1.5) - 0.5) * 1.5;
      const radius = CIRCLE_RADIUS + radiusVariation;
      const x = Math.sin(angle) * radius;
      const z = Math.cos(angle) * radius;
      
      // Deterministic stone properties based on index
      const heightVariation = (seededRandom(i * 2.3) - 0.5) * 1.5;
      const height = STONE_BASE_HEIGHT + heightVariation;
      const widthVariation = (seededRandom(i * 3.7) - 0.5) * 0.3;
      const width = STONE_WIDTH_BASE + widthVariation;
      const tilt = (seededRandom(i * 4.1) - 0.5) * 0.15;
      const rotationVariation = (seededRandom(i * 5.9) - 0.5) * 0.3;
      const rotation = angle + Math.PI / 2 + rotationVariation;
      
      result.push({
        position: [x, 0, z],
        rotation,
        height,
        width,
        tilt,
      });
    }
    return result;
  }, []);

  return (
    <group position={position}>
      {/* Standing stones in circle */}
      {stones.map((stone, index) => (
        <StandingStone
          key={index}
          position={stone.position}
          rotation={stone.rotation}
          height={stone.height}
          width={stone.width}
          tilt={stone.tilt}
        />
      ))}
      
      {/* Center altar stone */}
      <CenterAltar position={[0, 0, 0]} />
      
      {/* Ground circle (decorative) */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]} receiveShadow>
        <ringGeometry args={[CIRCLE_RADIUS - 2, CIRCLE_RADIUS + 2, 32]} />
        <meshStandardMaterial 
          color="#2d3748" 
          roughness={1} 
          metalness={0}
          transparent
          opacity={0.5}
        />
      </mesh>
      
      {/* Smaller inner ring */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.03, 0]} receiveShadow>
        <ringGeometry args={[1, 3, 24]} />
        <meshStandardMaterial 
          color="#1a202c" 
          roughness={1} 
          metalness={0}
          transparent
          opacity={0.6}
        />
      </mesh>
    </group>
  );
}
